/**
 * Admin Access Control Page
 *
 * Security Design:
 * - Admin can approve sharing requests, revoke access, extend expiry
 * - Approval grants encrypted blob access only
 * - Password shared out-of-band by owner
 * - Decryption always client-side; server never transmits password
 */

import React, { useState, useEffect } from 'react';
import { getAdminAccessRequests, adminApproveAccess, adminRevokeAccess, adminExtendAccess, requestOTP, verifyOTP } from '../services/api';
import { formatDateTime } from '../utils/time';

function AdminAccessControlPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [approveStep, setApproveStep] = useState('otp'); // 'otp' | 'confirm' — admin gets OTP, then sets expiry & approves
  const [approveOtpRequested, setApproveOtpRequested] = useState(false);
  const [approveOtpCode, setApproveOtpCode] = useState('');
  const [approveOtpLoading, setApproveOtpLoading] = useState(false);
  const [approveOtpMessage, setApproveOtpMessage] = useState('');
  const [expiryHours, setExpiryHours] = useState(24);
  const [extensionHours, setExtensionHours] = useState(24);
  const [revokeModal, setRevokeModal] = useState(null);
  const [revokeOtpRequested, setRevokeOtpRequested] = useState(false);
  const [revokeOtpCode, setRevokeOtpCode] = useState('');
  const [revokeOtpLoading, setRevokeOtpLoading] = useState(false);
  const [revokeOtpMessage, setRevokeOtpMessage] = useState('');

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminAccessRequests(statusFilter || null);
      setRequests(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load access requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const openApproveModal = (req) => {
    setApproveModal(req);
    setApproveStep('otp');
    setApproveOtpRequested(false);
    setApproveOtpCode('');
    setApproveOtpMessage('');
    setExpiryHours(24);
  };

  const handleApproveRequestOtp = async () => {
    if (!approveModal) return;
    setApproveOtpLoading(true);
    setApproveOtpMessage('');
    try {
      await requestOTP(approveModal.file_id, 'access_approve');
      setApproveOtpRequested(true);
      setApproveOtpMessage('OTP sent to your admin email. Enter the 6-digit code below. After approval, the file owner shares the password to the requester separately.');
    } catch (err) {
      setApproveOtpMessage(err.message || 'Failed to send OTP');
    } finally {
      setApproveOtpLoading(false);
    }
  };

  const handleApproveVerifyOtp = async () => {
    if (!approveModal || !approveOtpCode) return;
    setApproveOtpLoading(true);
    setApproveOtpMessage('');
    try {
      await verifyOTP(approveModal.file_id, approveOtpCode, 'access_approve');
      setApproveStep('confirm');
      setApproveOtpMessage('');
    } catch (err) {
      setApproveOtpMessage(err.message || 'Invalid or expired OTP');
    } finally {
      setApproveOtpLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setActionLoading(approveModal.id);
    setError(null);
    try {
      await adminApproveAccess(approveModal.id, expiryHours);
      setApproveModal(null);
      setApproveStep('otp');
      loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const openRevokeModal = (req) => {
    setRevokeModal(req);
    setRevokeOtpRequested(false);
    setRevokeOtpCode('');
    setRevokeOtpMessage('');
  };

  const handleRevokeRequestOtp = async () => {
    if (!revokeModal) return;
    setRevokeOtpLoading(true);
    setRevokeOtpMessage('');
    try {
      await requestOTP(revokeModal.file_id, 'revoke_access');
      setRevokeOtpRequested(true);
      setRevokeOtpMessage('OTP sent to your admin email. Enter the 6-digit code below.');
    } catch (err) {
      setRevokeOtpMessage(err.message || 'Failed to send OTP');
    } finally {
      setRevokeOtpLoading(false);
    }
  };

  const handleRevokeVerifyAndRevoke = async () => {
    if (!revokeModal || !revokeOtpCode || revokeOtpCode.length < 6) return;
    setRevokeOtpLoading(true);
    setRevokeOtpMessage('');
    try {
      await verifyOTP(revokeModal.file_id, revokeOtpCode, 'revoke_access');
      await adminRevokeAccess(revokeModal.id);
      setRevokeModal(null);
      setRevokeOtpRequested(false);
      setRevokeOtpCode('');
      loadRequests();
    } catch (err) {
      setRevokeOtpMessage(err.message || 'Invalid OTP or failed to revoke');
    } finally {
      setRevokeOtpLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!extendModal) return;
    setActionLoading(extendModal.id);
    setError(null);
    try {
      await adminExtendAccess(extendModal.id, extensionHours);
      setExtendModal(null);
      loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to extend');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'PENDING': return { bg: 'var(--yellow-muted)', color: 'var(--yellow)' };
      case 'APPROVED': return { bg: 'var(--green-muted)', color: 'var(--green)' };
      case 'REJECTED': return { bg: 'var(--red-muted)', color: 'var(--red)' };
      case 'REVOKED': return { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
      default: return { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
    }
  };

  const pendingCount = requests.filter(r => r.status === 'PENDING').length;
  const approvedCount = requests.filter(r => r.status === 'APPROVED').length;

  const rejectedCount = requests.filter(r => r.status === 'REJECTED').length;
  const revokedCount = requests.filter(r => r.status === 'REVOKED').length;

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 80, gap: 16 }}>
        <div style={{ width: 32, height: 32, borderWidth: 3, borderStyle: 'solid', borderColor: 'rgba(99,102,241,0.15)', borderTopColor: '#6366F1', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>Loading access requests...</span>
      </div>
    );
  }

  return (
    <div className="page-container admin-page-shell">
      <style>{`
        .ac-row { transition: all 0.15s ease; }
        .ac-row:hover { background: var(--bg-hover) !important; }
        .ac-stat-card { transition: all 0.25s cubic-bezier(0.22,1,0.36,1); cursor: default; position: relative; overflow: hidden; }
        .ac-stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .ac-stat-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: 12px 12px 0 0; opacity: 0.8; }
        .ac-stat-total::before { background: linear-gradient(90deg, #6366F1, #8B5CF6); }
        .ac-stat-pending::before { background: linear-gradient(90deg, #F59E0B, #FBBF24); }
        .ac-stat-approved::before { background: linear-gradient(90deg, #10B981, #34D399); }
        .ac-stat-rejected::before { background: linear-gradient(90deg, #EF4444, #F87171); }
        .ac-btn-extend { transition: all 0.2s ease; }
        .ac-btn-extend:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(99,102,241,0.3) !important; }
        .ac-btn-revoke { transition: all 0.2s ease; }
        .ac-btn-revoke:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(239,68,68,0.3) !important; }
        .ac-btn-approve { transition: all 0.2s ease; }
        .ac-btn-approve:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(34,197,94,0.3) !important; }
        .ac-refresh:hover { background: var(--bg-hover) !important; color: #6366F1 !important; border-color: rgba(99,102,241,0.3) !important; }
        .ac-filter-chip { transition: all 0.2s ease; cursor: pointer; }
        .ac-filter-chip:hover { border-color: rgba(99,102,241,0.3) !important; background: var(--bg-hover) !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={s.headerRow}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={s.headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 style={s.pageTitle}>Access Control</h2>
          </div>
          <p style={s.subtitle}>Manage sharing permissions — approve, revoke, or extend access to encrypted files.</p>
        </div>
        <button onClick={loadRequests} className="ac-refresh" style={s.refreshBtn} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </button>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={s.errorBar} role="alert">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>{error}{error.includes('403') || error.toLowerCase().includes('admin') ? ' Ensure you are logged in as admin.' : ''}</span>
        </div>
      )}

      {/* ── Stats Cards ── */}
      <div style={s.summaryBar}>
        <div className="ac-stat-card ac-stat-total" style={s.summaryCard}>
          <div style={s.summaryIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div style={s.summaryNumber}>{requests.length}</div>
          <div style={s.summaryLabel}>Total Requests</div>
        </div>
        <div className="ac-stat-card ac-stat-pending" style={s.summaryCard}>
          <div style={s.summaryIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div style={{ ...s.summaryNumber, color: '#F59E0B' }}>{pendingCount}</div>
          <div style={s.summaryLabel}>Pending</div>
        </div>
        <div className="ac-stat-card ac-stat-approved" style={s.summaryCard}>
          <div style={s.summaryIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div style={{ ...s.summaryNumber, color: '#10B981' }}>{approvedCount}</div>
          <div style={s.summaryLabel}>Approved</div>
        </div>
        <div className="ac-stat-card ac-stat-rejected" style={s.summaryCard}>
          <div style={s.summaryIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <div style={{ ...s.summaryNumber, color: '#EF4444' }}>{rejectedCount + revokedCount}</div>
          <div style={s.summaryLabel}>Rejected / Revoked</div>
        </div>
      </div>

      {/* ── Filter Row ── */}
      <div style={s.filterRow}>
        <div style={s.filterIcon}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
        </div>
        {['', 'PENDING', 'APPROVED', 'REJECTED', 'REVOKED'].map((val) => (
          <button
            key={val}
            className="ac-filter-chip"
            onClick={() => setStatusFilter(val)}
            style={{
              ...s.filterChip,
              ...(statusFilter === val ? s.filterChipActive : {}),
            }}
          >
            {val || 'All'}
            {val === 'PENDING' && pendingCount > 0 && <span style={s.filterBadge}>{pendingCount}</span>}
          </button>
        ))}
      </div>

      {/* ── Table ── */}
      <div style={s.tableContainer}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>File</th>
              <th style={s.th}>Owner</th>
              <th style={s.th}>Requester</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Expiry</th>
              <th style={s.th}>Created</th>
              <th style={{ ...s.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req, i) => {
              const statusStyle = getStatusStyle(req.status);
              const isLoading = actionLoading === req.id;
              return (
                <tr key={req.id} className="ac-row" style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(99,102,241,0.015)' }}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/>
                        <line x1="9" y1="15" x2="15" y2="15"/>
                      </svg>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 12 }}>{req.file_name}</span>
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, color: 'var(--text-body)' }}>{req.owner_email}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, color: 'var(--text-body)' }}>{req.requester_email}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 99,
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
                      backgroundColor: statusStyle.bg, color: statusStyle.color,
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: statusStyle.color, flexShrink: 0 }} />
                      {req.status}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, color: req.expiry_time ? 'var(--text-body)' : 'var(--text-muted)' }}>
                      {req.expiry_time ? formatDateTime(req.expiry_time) : '—'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {req.created_at ? formatDateTime(req.created_at) : '—'}
                    </span>
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                      {req.status === 'PENDING' && (
                        <button
                          onClick={() => openApproveModal(req)}
                          disabled={isLoading}
                          className="ac-btn-approve"
                          style={s.approveBtn}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Approve
                        </button>
                      )}
                      {req.status === 'APPROVED' && (
                        <>
                          <button
                            onClick={() => setExtendModal(req)}
                            disabled={isLoading}
                            className="ac-btn-extend"
                            style={s.extendBtn}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            Extend
                          </button>
                          <button
                            onClick={() => openRevokeModal(req)}
                            disabled={isLoading}
                            className="ac-btn-revoke"
                            style={s.revokeBtn}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            Revoke
                          </button>
                        </>
                      )}
                      {(req.status === 'REJECTED' || req.status === 'REVOKED') && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>No actions</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {requests.length === 0 && (
          <div style={s.emptyState}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '10px 0 0', fontWeight: 500 }}>No access requests found.</p>
            <p style={{ color: 'var(--text-faint)', fontSize: 11, margin: '4px 0 0' }}>Requests will appear here when users request file access.</p>
          </div>
        )}
      </div>

      {/* Approve Modal — Step 1: Admin gets OTP. Step 2: Set expiry & approve. Owner shares password to requester separately. */}
      {approveModal && (
        <div style={s.modalOverlay} onClick={() => { setApproveModal(null); setApproveStep('otp'); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={{ ...s.modalIconWrap, background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(16,185,129,0.05))' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div>
                <h3 style={s.modalTitle}>Approve Access</h3>
                <p style={s.modalSubtitle}>Grant file sharing. File owner shares password to requester separately.</p>
              </div>
              <button onClick={() => { setApproveModal(null); setApproveStep('otp'); }} style={s.modalCloseBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={s.modalInfoCard}>
              <div style={s.modalInfoRow}>
                <span style={s.modalInfoLabel}>Requester</span>
                <span style={s.modalInfoValue}>{approveModal.requester_email}</span>
              </div>
              <div style={{ ...s.modalInfoRow, border: 'none' }}>
                <span style={s.modalInfoLabel}>File</span>
                <span style={s.modalInfoValue}>{approveModal.file_name}</span>
              </div>
            </div>

            <div style={s.modalStepIndicator}>
              <div style={{ ...s.stepDot, background: '#6366F1' }}>1</div>
              <div style={{ ...s.stepLine, background: approveStep === 'confirm' ? '#6366F1' : 'var(--border)' }} />
              <div style={{ ...s.stepDot, background: approveStep === 'confirm' ? '#6366F1' : 'var(--border)' }}>2</div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 8 }}>
                {approveStep === 'otp' ? 'Admin OTP' : 'Set expiry & approve'}
              </span>
            </div>

            {approveStep === 'otp' && (
              <>
                {approveOtpMessage && (
                  <div style={{
                    fontSize: 12, padding: '8px 12px', marginBottom: 12, borderRadius: 8,
                    display: 'flex', alignItems: 'center', gap: 8,
                    backgroundColor: approveOtpMessage.startsWith('OTP sent') ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                    color: approveOtpMessage.startsWith('OTP sent') ? '#10B981' : '#EF4444',
                    border: `1px solid ${approveOtpMessage.startsWith('OTP sent') ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}`,
                  }}>
                    {approveOtpMessage}
                  </div>
                )}
                {!approveOtpRequested ? (
                  <button onClick={handleApproveRequestOtp} disabled={approveOtpLoading} style={s.primaryBtn}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {approveOtpLoading ? 'Sending OTP...' : 'Send OTP to my admin email'}
                  </button>
                ) : (
                  <>
                    <label style={s.modalLabel}>Enter 6-digit OTP</label>
                    <input
                      type="text"
                      value={approveOtpCode}
                      onChange={(e) => setApproveOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={e => { if (e.key === 'Enter' && approveOtpCode.length === 6) handleApproveVerifyOtp(); }}
                      placeholder="• • • • • •"
                      maxLength={6}
                      style={s.otpInput}
                    />
                    <div style={s.modalActions}>
                      <button onClick={() => { setApproveModal(null); setApproveStep('otp'); }} style={s.cancelBtn}>Cancel</button>
                      <button onClick={handleApproveVerifyOtp} disabled={approveOtpCode.length < 6 || approveOtpLoading} style={s.confirmBtn}>
                        {approveOtpLoading ? 'Verifying...' : 'Verify OTP'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {approveStep === 'confirm' && (
              <>
                <div style={{ fontSize: 12, color: '#10B981', marginBottom: 14, padding: '8px 12px', backgroundColor: 'rgba(34,197,94,0.08)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(34,197,94,0.15)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  OTP verified. Requester can download encrypted file. File owner shares decryption password to requester (e.g. WhatsApp, email).
                </div>
                <div style={s.modalField}>
                  <label style={s.modalLabel}>Access expiry (hours)</label>
                  <input type="number" min={1} max={720} value={expiryHours} onChange={(e) => setExpiryHours(parseInt(e.target.value, 10) || 24)} style={s.input} />
                </div>
                <div style={s.modalActions}>
                  <button onClick={() => { setApproveModal(null); setApproveStep('otp'); }} style={s.cancelBtn}>Cancel</button>
                  <button onClick={handleApprove} disabled={actionLoading} style={s.confirmBtn}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Approve Access
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Revoke Modal — Admin gets OTP, then revoke */}
      {revokeModal && (
        <div style={s.modalOverlay} onClick={() => { setRevokeModal(null); setRevokeOtpRequested(false); setRevokeOtpCode(''); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={{ ...s.modalIconWrap, background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <div>
                <h3 style={s.modalTitle}>Revoke Access</h3>
                <p style={s.modalSubtitle}>Remove file sharing permission. Admin OTP required.</p>
              </div>
              <button onClick={() => { setRevokeModal(null); setRevokeOtpRequested(false); setRevokeOtpCode(''); }} style={s.modalCloseBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={s.modalInfoCard}>
              <div style={s.modalInfoRow}>
                <span style={s.modalInfoLabel}>Requester</span>
                <span style={s.modalInfoValue}>{revokeModal.requester_email}</span>
              </div>
              <div style={{ ...s.modalInfoRow, border: 'none' }}>
                <span style={s.modalInfoLabel}>File</span>
                <span style={s.modalInfoValue}>{revokeModal.file_name}</span>
              </div>
            </div>

            {revokeOtpMessage && (
              <div style={{
                fontSize: 12, padding: '8px 12px', marginBottom: 12, borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 8,
                backgroundColor: revokeOtpMessage.startsWith('OTP sent') ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                color: revokeOtpMessage.startsWith('OTP sent') ? '#10B981' : '#EF4444',
                border: `1px solid ${revokeOtpMessage.startsWith('OTP sent') ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}`,
              }}>
                {revokeOtpMessage}
              </div>
            )}
            {!revokeOtpRequested ? (
              <div style={s.modalActions}>
                <button onClick={() => { setRevokeModal(null); setRevokeOtpRequested(false); }} style={s.cancelBtn}>Cancel</button>
                <button onClick={handleRevokeRequestOtp} disabled={revokeOtpLoading} style={s.primaryBtn}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {revokeOtpLoading ? 'Sending OTP...' : 'Send OTP to my admin email'}
                </button>
              </div>
            ) : (
              <>
                <label style={s.modalLabel}>Enter 6-digit OTP</label>
                <input
                  type="text"
                  value={revokeOtpCode}
                  onChange={(e) => setRevokeOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={e => { if (e.key === 'Enter' && revokeOtpCode.length === 6) handleRevokeVerifyAndRevoke(); }}
                  placeholder="• • • • • •"
                  maxLength={6}
                  style={s.otpInput}
                />
                <div style={s.modalActions}>
                  <button onClick={() => { setRevokeModal(null); setRevokeOtpRequested(false); setRevokeOtpCode(''); }} style={s.cancelBtn}>Cancel</button>
                  <button
                    onClick={handleRevokeVerifyAndRevoke}
                    disabled={revokeOtpCode.length < 6 || revokeOtpLoading}
                    style={s.dangerBtn}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                    {revokeOtpLoading ? 'Revoking...' : 'Verify OTP & Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {extendModal && (
        <div style={s.modalOverlay} onClick={() => setExtendModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={{ ...s.modalIconWrap, background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.05))' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <h3 style={s.modalTitle}>Extend Access</h3>
                <p style={s.modalSubtitle}>Extend sharing duration</p>
              </div>
              <button onClick={() => setExtendModal(null)} style={s.modalCloseBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={s.modalInfoCard}>
              <div style={s.modalInfoRow}>
                <span style={s.modalInfoLabel}>Requester</span>
                <span style={s.modalInfoValue}>{extendModal.requester_email}</span>
              </div>
              <div style={{ ...s.modalInfoRow, border: 'none' }}>
                <span style={s.modalInfoLabel}>File</span>
                <span style={s.modalInfoValue}>{extendModal.file_name}</span>
              </div>
            </div>

            <div style={s.modalField}>
              <label style={s.modalLabel}>Extension duration (hours)</label>
              <input type="number" min={1} max={720} value={extensionHours} onChange={(e) => setExtensionHours(parseInt(e.target.value, 10) || 24)} style={s.input} />
            </div>
            <div style={s.modalActions}>
              <button onClick={() => setExtendModal(null)} style={s.cancelBtn}>Cancel</button>
              <button onClick={handleExtend} disabled={actionLoading} style={s.confirmBtn}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Extend Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Security Footer ── */}
      <div style={s.securityNote}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span><strong>Zero-knowledge architecture:</strong> Approval grants encrypted blob access only. Server never transmits passwords. All decryption happens client-side.</span>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: '-0.5px', lineHeight: 1.2,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '2px 0 0', lineHeight: 1.5, fontWeight: 400 },
  headerIcon: {
    width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12, flexWrap: 'wrap' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 36, height: 36, borderRadius: 10,
    border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)',
    cursor: 'pointer', color: 'var(--text-secondary)',
    transition: 'all 0.2s ease',
  },
  errorBar: {
    backgroundColor: 'rgba(239,68,68,0.06)', color: '#EF4444', padding: '10px 14px', borderRadius: 10,
    marginBottom: 20, border: '1px solid rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
  },
  summaryBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 22 },
  summaryCard: {
    backgroundColor: 'var(--bg-card)', padding: '16px 18px', borderRadius: 14,
    boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)',
    position: 'relative', overflow: 'hidden',
  },
  summaryIcon: {
    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'var(--bg-elevated)', marginBottom: 10,
  },
  summaryNumber: { fontSize: 26, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.5px', lineHeight: 1 },
  summaryLabel: { fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' },
  filterRow: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18, flexWrap: 'wrap',
  },
  filterIcon: { color: 'var(--text-muted)', display: 'flex', marginRight: 2 },
  filterChip: {
    padding: '5px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)',
    display: 'inline-flex', alignItems: 'center', gap: 4, letterSpacing: '0.2px',
  },
  filterChipActive: {
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: '1px solid transparent', boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
  },
  filterBadge: {
    fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.25)', color: '#fff',
    padding: '1px 5px', borderRadius: 99, lineHeight: 1.4,
  },
  tableContainer: {
    backgroundColor: 'var(--bg-card)', borderRadius: 14,
    boxShadow: 'var(--shadow)', border: '1px solid var(--border)',
    overflow: 'auto', marginBottom: 20,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: 10,
    background: 'var(--bg-elevated)',
    fontWeight: 700, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
  },
  td: { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-body)', verticalAlign: 'middle' },
  approveBtn: {
    padding: '5px 12px', borderRadius: 6, fontSize: 11,
    border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)',
    color: '#fff', fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(34,197,94,0.2)',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  extendBtn: {
    padding: '5px 12px', borderRadius: 6, fontSize: 11,
    border: '1px solid rgba(99,102,241,0.2)', background: 'rgba(99,102,241,0.06)',
    color: '#6366F1', fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  revokeBtn: {
    padding: '5px 12px', borderRadius: 6, fontSize: 11,
    border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)',
    color: '#EF4444', fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  emptyState: { textAlign: 'center', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' },

  /* ── Modals ── */
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    backdropFilter: 'blur(8px)',
  },
  modal: {
    backgroundColor: 'var(--bg-card)', borderRadius: 16,
    maxWidth: 420, width: '100%', padding: 24,
    boxShadow: 'var(--shadow-xl)',
    border: '1px solid var(--border)',
    color: 'var(--text-body)',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
    paddingBottom: 16, borderBottom: '1px solid var(--border)',
  },
  modalIconWrap: {
    width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  modalCloseBtn: {
    marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-muted)', padding: 4, display: 'flex', borderRadius: 6,
    transition: 'all 0.15s ease',
  },
  modalTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.3px' },
  modalSubtitle: { margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 },
  modalInfoCard: {
    background: 'var(--bg-elevated)', borderRadius: 10, padding: '2px 14px',
    marginBottom: 16, border: '1px solid var(--border-subtle)',
  },
  modalInfoRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
  },
  modalInfoLabel: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  modalInfoValue: { fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right', wordBreak: 'break-all', maxWidth: '60%' },
  modalStepIndicator: {
    display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16,
    padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8,
  },
  stepDot: {
    width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0,
  },
  stepLine: { height: 2, width: 32, flexShrink: 0 },
  modalText: { margin: '0 0 8px', fontSize: 13, color: 'var(--text-body)' },
  modalNote: { margin: '0 0 14px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 },
  modalField: { marginBottom: 16 },
  modalLabel: { display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid var(--border)', backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  otpInput: {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1.5px solid var(--border)', backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: 20, boxSizing: 'border-box',
    fontFamily: "'JetBrains Mono', monospace", letterSpacing: 10, textAlign: 'center',
    marginBottom: 14, fontWeight: 600,
    transition: 'border-color 0.2s ease',
  },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
  cancelBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all 0.15s ease',
  },
  confirmBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 700,
    boxShadow: '0 2px 8px rgba(34,197,94,0.25)',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.2s ease',
  },
  primaryBtn: {
    padding: '10px 18px', borderRadius: 8, border: 'none', width: '100%',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 700,
    boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all 0.2s ease', marginBottom: 4,
  },
  dangerBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 700,
    boxShadow: '0 2px 8px rgba(239,68,68,0.25)',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.2s ease',
  },
  securityNote: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', padding: '12px 16px',
    borderRadius: 12, fontSize: 12, fontWeight: 400, lineHeight: 1.5,
  },
};

export default AdminAccessControlPage;
